'use strict'
import { useCallback } from 'react'
import { useEmployeeCountDispatch, useEmployeeCountSelector } from '../extra/store'
import { ShowErrors } from './ShowErrors'
import { shallowEqual } from 'react-redux'

/*==================================
 定数定義
 ==================================*/
const ROUNDING_METHOD_OPTIONS = [
  {
    value: '切り捨て',
    label: '切り捨て（例：500.5人→500人）'
  },
  {
    value: '四捨五入',
    label: '四捨五入（例：500.5人→501人）'
  }
]

/*==================================
 カスタムフック
 ==================================*/
/**
 * 小数点の扱いに関する設定のロジックを管理するカスタムフック。
 *
 * @returns {{
 *   roundingMethod: string
 *   fiscalYearErrors: object
 *   handleChangeRoundingMethod: (e: React.ChangeEvent<HTMLInputElement>) => void
 * }}
 */
const useRoundingMethodLogic = () => {
  const dispatch = useEmployeeCountDispatch()
  const { roundingMethod, fiscalYearErrors } = useEmployeeCountSelector((s) => {
    const selectedYear = s.employeeCountInfo.selectedFiscalYear
    const currentSettings = s.employeeCountInfo.fiscalYearSettings[selectedYear]
    return {
      roundingMethod: currentSettings?.employeeCountSetting?.[0]?.roundingMethod,
      fiscalYearErrors: currentSettings?.errors?.client
    }
  }, shallowEqual)

  /**
   * 小数点の扱いに関する設定変更処理（変更アクションをdispatch）
   *
   * @param {string} employmentType - 変更対象の雇用区分（キー）
   * @returns {(e: React.ChangeEvent<HTMLInputElement>) => void} - イベント（小数点の扱いに関する設定（入力値））
   */
  const handleChangeRoundingMethod = useCallback(
    (e) => {
      dispatch('UPDATE_ROUNDING_METHOD', { roundingMethod: e.target.value })
    },
    [dispatch]
  )

  return { roundingMethod, fiscalYearErrors, handleChangeRoundingMethod }
}

/*==================================
 コンポーネント
 ==================================*/
/** 小数点の扱いに関する設定コンポーネント */
const RoundingMethodSetting = () => {
  const { roundingMethod, fiscalYearErrors, handleChangeRoundingMethod } = useRoundingMethodLogic()

  return (
    <>
      <div className="text-body-2-bold grow mb-2 ">■小数点の扱いに関する設定</div>
      <div className="flex items-center justify-between mb-6">
        従業員数を算出した結果、小数点以下の値が生じることがあります。
        <br />
        従業員数を整数に補正する際の小数点以下の扱いについて、以下の方法から選択してください。
      </div>
      <div className="flex rounded-xl">
        <dl className="flex px-4 items-center">
          <dd>
            <ul className="flex gap-x-8">
              {ROUNDING_METHOD_OPTIONS.map((option) => {
                const key = `roundingMethod_${option.value}`
                return (
                  <li key={key}>
                    <label
                      role="button"
                      className="ui-radio focus-within:ring-2 ring-gray-900 ring-offset-2 "
                      htmlFor={key}
                    >
                      <input
                        type="radio"
                        id={key}
                        name={option.value}
                        className="ui-radio__input  "
                        value={option.value}
                        checked={roundingMethod === option.value}
                        onChange={handleChangeRoundingMethod}
                      />
                      <span className="ui-radio__icon"></span>
                      <span className="ui-radio__ripple"></span>
                      <span className="ui-radio__label  !text-base">{option.label}</span>
                    </label>
                  </li>
                )
              })}
            </ul>
            {/* エラーメッセージ表示コンポーネント */}
            <ShowErrors clientErrors={fiscalYearErrors} fieldName="roundingMethod" />
          </dd>
        </dl>
      </div>
    </>
  )
}

export default RoundingMethodSetting
